import { Form } from "../../../../../components/common/Forms";
import AdminTuitionErrorBanner from "../../../../../components/common/AdminTuitionErrorBanner";
import tuitionStepFields, {
  commentTypeMap,
  courseInfoFields,
  vendorInfoFields,
  personalInfoFields,
  approverInfoFields,
} from "../../../../../config/tuitionFormConfig";
import { FC, useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../store/store";
import Loader from "../../../../../components/common/Loader";
import {
  downloadPrintApplication,
  downloadPrintFaxCover,
  fetchAllApplicationDetails,
  updateAdminApplicationDetails,
} from "../../../../../store/slices/Tuition/tuitionAdminSlice";
import {
  fetchDropdownOptions,
  fetchMilesData,
} from "../../../../../store/slices/Tuition/tuitionApplySlice";
import { validateTuitionForm } from "../../../../../utils/tuitionValidationEngine";
import { formatDateYMDToMDY, formatTimestamp } from "../../../../../utils/date";
import {
  trackButtonClick,
  trackFormSave,
  trackFormError,
} from "../../../../../services/analytics";
import { safeGet } from "../../../../../utils/safeGet";
import Notifications from "../../../../../components/common/Notifications";
import UploadErrorBanner from "../../../../CitizenshipReimbursement/UploadDocument/ViewDetails/UploadErrorBanner";
import endpoints from "../../../../../api/endpoints";

const ADMIN_SCHOOL_NAME_SUGGESTIONS_ENDPOINT =
  endpoints.tuition.tuitionApply.getAdminSchoolNameSuggestions;

// ===== FIELD MAPPING CONFIGURATION =====
// Maps form field names to their nested payload structure
const FIELD_MAPPING = {
  // Employee Info Fields
  empAlternatePhone: { section: "employeeInfo", key: "altPhone" },
  empPrefPhone: { section: "employeeInfo", key: "prefPhone" },
  empHomeEmail: { section: "employeeInfo", key: "homeEmail" },
  empPrefEmail: { section: "employeeInfo", key: "prefEmail" },
  mgrPhone: { section: "employeeInfo", key: "managerPhone" },
  altPhone: { section: "employeeInfo", key: "altManagerPhone" },

  // School Info Fields
  programField: { section: "schoolInfo", key: "programField" },
  programType: { section: "schoolInfo", key: "programType" },
  degreeName: { section: "schoolInfo", key: "degreeName" },
  termBegin: { section: "schoolInfo", key: "termBegin" },
  termEnd: { section: "schoolInfo", key: "termEnd" },
  applicationTerm: { section: "schoolInfo", key: "applicationTerm" },
  schoolName: { section: "schoolInfo", key: "schoolName" },
  schoolUrl: { section: "schoolInfo", key: "schoolUrl" },
  degreeCompletionYear: { section: "schoolInfo", key: "degreeCompletionYear" },
  fundingSource: { section: "schoolInfo", key: "fundingSource" },
} as const;

// Helper function to build field labels from all field configurations
const getFieldLabels = (): Record<string, string> => {
  const labels: Record<string, string> = {};

  // Add personal info field labels
  personalInfoFields.forEach((field) => {
    labels[field.name] = field.label;
  });

  // Add approver info field labels
  approverInfoFields.forEach((field) => {
    labels[field.name] = field.label;
  });

  // Add vendor/school info field labels
  vendorInfoFields.forEach((field) => {
    labels[field.name] = field.label;
  });

  // Add course info field labels
  courseInfoFields.forEach((field) => {
    labels[field.name] = field.label;
  });

  return labels;
};

// ===== HELPER FUNCTIONS FOR DATA TRANSFORMATION =====

/**
 * Converts field values to appropriate types for API payload
 */
const convertFieldType = (fieldName: string, value: any): any => {
  // If value is empty, return as is
  if (value === undefined || value === null || value === "") {
    return value;
  }

  // Numeric course fields
  const numericFields = [
    "courseCredit",
    "estimatedTuitionFee",
    "estimatedBooksFee",
    "estimatedLabFee",
    "estimatedRegistrationFee",
    "estimatedOtherFee",
    "estimatedAirfareBaggageFee",
    "estimatedAutoRentalFee",
    "estimatedHotelFee",
    "estimatedParkTollFee",
    "estimatedPublicTransportFee",
    "estimatedMilesDollarValue",
    "totalNumberOfMiles",
    "numberOfTravelDays",
  ];

  if (numericFields.includes(fieldName)) {
    const num = Number(value);
    return Number.isNaN(num) ? value : num;
  }

  return value;
};

// Format dates to MM-DD-YYYY
const formatDate = (val: any) => {
  if (!val || typeof val !== "string") return val;
  // ISO format YYYY-MM-DD or with time
  if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
    const formatted = val.split("T")[0].split("-").reverse().join("-");
    return formatted;
  }
  // MM/DD/YYYY format (from date picker) - just replace / with -
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
    const formatted = val.split("/").join("-");
    return formatted;
  }
  // DD/MM/YYYY format - swap to MM/DD/YYYY then convert
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(val)) {
    const parts = val.split("/");
    if (parts[0].length === 1) parts[0] = "0" + parts[0];
    if (parts[1].length === 1) parts[1] = "0" + parts[1];
    const formatted = `${parts[0]}-${parts[1]}-${parts[2]}`;
    return formatted;
  }
  return val;
};

// Validate month and day values from a MM/DD/YYYY-like string
const validateMonthDay = (dateFormatted: string, showAlerts: boolean): boolean => {
  if (!dateFormatted) return true;
  const parts = dateFormatted.split("/");
  if (parts.length !== 3) return true; // nothing to validate
  const month = Number.parseInt(parts[0], 10);
  const day = Number.parseInt(parts[1], 10);
  if (month > 12) {
    if (showAlerts) {
      alert("Invalid month. Month must be between 1 and 12.");
    }
    return false;
  }
  if (day > 31) {
    if (showAlerts) {
      alert("Invalid day. Day must be between 1 and 31.");
    }
    return false;
  }
  return true;
};

/**
 * Flattens nested employeeInfo and schoolInfo into form field names
 * Converts API response structure to form field structure
 */
const flattenApplicationData = (data: any): Record<string, any> => {
  const flattened = { ...data };

  // Flatten employeeInfo fields
  if (data?.employeeInfo) {
    Object.entries(FIELD_MAPPING).forEach(([formField, mapping]) => {
      if (
        mapping.section === "employeeInfo" &&
        data.employeeInfo[mapping.key] !== undefined
      ) {
        flattened[formField] = data.employeeInfo[mapping.key];
      }
    });
  }

  // Flatten schoolInfo fields
  if (data?.schoolInfo) {
    Object.entries(FIELD_MAPPING).forEach(([formField, mapping]) => {
      if (
        mapping.section === "schoolInfo" &&
        data.schoolInfo[mapping.key] !== undefined
      ) {
        flattened[formField] = data.schoolInfo[mapping.key];
      }
    });
    // Also extract applicationYear from schoolInfo if present
    if (data.schoolInfo.applicationYear !== undefined) {
      flattened.applicationYear = data.schoolInfo.applicationYear;
    }
  }
  // Fallback: extract applicationYear from top-level if present
  if (data.applicationYear !== undefined) {
    flattened.applicationYear = data.applicationYear;
  }

  return flattened;
};

/**
 * Handles applicationYear changes for schoolInfo
 */
const handleApplicationYearChange = (
  editedValues: Record<string, any>,
  originalValues: Record<string, any>,
  schoolInfoChanges: Record<string, any>,
): void => {
  if (
    editedValues.applicationYear !== undefined &&
    editedValues.applicationYear !== null &&
    editedValues.applicationYear !== originalValues.applicationYear
  ) {
    schoolInfoChanges.applicationYear = editedValues.applicationYear;
  }
};

/**
 * Converts "Non Degree Program" to special sentinel value "-100" for degreeCompletionYear
 */
const convertNonDegreeProgramValue = (
  schoolInfoChanges: Record<string, any>,
): void => {
  if (schoolInfoChanges.degreeCompletionYear === "Non Degree Program") {
    schoolInfoChanges.degreeCompletionYear = "-100";
  }
};

/**
 * Builds payload with only changed values in proper nested structure
 * Handles employeeInfo, schoolInfo, and course updates
 */
const buildPayload = (
  editedValues: Record<string, any>,
  originalValues: Record<string, any>,
  deletedCourseIds: Array<string | number> = [],
  deletedCommentIds: number[] = [],
): Record<string, any> => {
  const payload: Record<string, any> = {};
  let employeeInfoChanges: Record<string, any> = {};
  let schoolInfoChanges: Record<string, any> = {};
  let updatedCourses: Array<Record<string, any>> = [];
  let newCourses: Array<Record<string, any>> = [];
  let newComments: Array<Record<string, any>> = [];

  // Flatten both edited and original values to ensure consistent comparison
  const flattenedEdited = flattenApplicationData(editedValues);
  const flattenedOriginal = flattenApplicationData(originalValues);

  // Iterate through all mapped fields to check for changes
  Object.entries(FIELD_MAPPING).forEach(([formField, mapping]) => {
    // Get new value - check flattened version and also nested structures
    const newValue = flattenedEdited[formField] ?? editedValues[formField];
    const originalValue =
      flattenedOriginal[formField] ?? originalValues[formField];

    // Only include if value has changed
    if (
      newValue !== undefined &&
      newValue !== null &&
      newValue !== originalValue
    ) {
      if (mapping.section === "employeeInfo") {
        employeeInfoChanges[mapping.key] = newValue;
      } else if (mapping.section === "schoolInfo") {
        // Format dates for schoolInfo
        const formattedValue = ["termBegin", "termEnd"].includes(mapping.key)
          ? formatDate(newValue)
          : newValue;
        schoolInfoChanges[mapping.key] = formattedValue;
      }
    }
  });

  // Handle applicationYear separately for schoolInfo
  handleApplicationYearChange(editedValues, originalValues, schoolInfoChanges);

  // Convert "Non Degree Program" to special value "-100"
  convertNonDegreeProgramValue(schoolInfoChanges);
  // Handle course updates - compare original and edited courses
  const originalCourses = safeGet(originalValues?.courses, []);
  const editedCourses = safeGet(editedValues?.courses, []);

  if (editedCourses.length > 0) {
    editedCourses.forEach((editedCourse: any) => {
      const courseId =
        editedCourse.employeeCourseId ||
        editedCourse.courseId ||
        editedCourse.id;
      // Find matching original course by ID
      const originalCourse = originalCourses.find((oc: any) => {
        const ocId = oc.employeeCourseId || oc.courseId || oc.id;
        return ocId === courseId;
      });
      // Check if it's a new course (no database ID, or ID is a string with timestamp pattern)
      const isNewCourse =
        !courseId ||
        (typeof courseId === "string" && /^\d+-\d+$/.test(courseId));

      if (isNewCourse) {
        // New course - collect all fields
        const newCourse: Record<string, any> = {};
        Object.keys(editedCourse).forEach((key) => {
          const value = editedCourse[key];

          // Skip internal IDs
          if (
            [
              "id",
              "courseId",
              "employeeCourseId",
              "lastUpdatedBy",
              "lastUpdatedTime",
            ].includes(key)
          ) {
            return;
          }

          if (value !== undefined && value !== null && value !== "") {
            // Convert field type and format dates for new courses
            let convertedValue = convertFieldType(key, value);
            if (["courseStartDate", "courseEndDate"].includes(key)) {
              convertedValue = formatDate(convertedValue);
            }
            // Map form field names to API field names
            let fieldKey = key;
            if (key === "programType") fieldKey = "courseProgramType";
            else if (key === "originZip") fieldKey = "originZipCode";
            else if (key === "destinationZip") fieldKey = "destinationZipCode";
            newCourse[fieldKey] = convertedValue;
          }
        });

        if (Object.keys(newCourse).length > 0) {
          newCourses.push(newCourse);
        }
      } else if (originalCourse) {
        // Course exists - check for changes
        const courseChanges: Record<string, any> = {
          courseId:
            originalCourse.employeeCourseId ||
            originalCourse.id ||
            originalCourse.courseId,
        };

        let hasChanges = false;

        // Compare all fields in the course
        Object.keys(editedCourse).forEach((key) => {
          const newValue = editedCourse[key];
          const originalValue = originalCourse[key];

          // Skip certain fields
          if (
            ["id", "courseId", "lastUpdatedBy", "lastUpdatedTime"].includes(key)
          ) {
            return;
          }

          // Check if value changed
          if (newValue !== undefined && newValue !== originalValue) {
            // Convert field type and format dates for courses
            let convertedValue = convertFieldType(key, newValue);
            if (["courseStartDate", "courseEndDate"].includes(key)) {
              convertedValue = formatDate(convertedValue);
            }
            // Map form field names to API field names
            let fieldKey = key;
            if (key === "programType") fieldKey = "courseProgramType";
            else if (key === "originZip") fieldKey = "originZipCode";
            else if (key === "destinationZip") fieldKey = "destinationZipCode";
            courseChanges[fieldKey] = convertedValue;
            hasChanges = true;
          }
        });

        // Only add if there are changes
        if (hasChanges) {
          updatedCourses.push(courseChanges);
        }
      }
    });
  }

  // Handle new comments - detect when a comment field now has value but didn't before
  const commentFields = [
    "employeeComments",
    "employmentJobComments",
    "noEstimatedFees",
  ];
  let updatedComments: Array<Record<string, any>> = [];

  commentFields.forEach((fieldName) => {
    const originalValue = flattenedOriginal[fieldName];
    const editedValue = flattenedEdited[fieldName];

    // Check if comment is new (no original value but now has value)
    if (
      (!originalValue || originalValue.trim().length === 0) &&
      editedValue &&
      editedValue.trim().length > 0
    ) {
      const commentType = (commentTypeMap as any)?.[fieldName];
      if (commentType) {
        newComments.push({
          comments: editedValue,
          commentType: commentType,
        });
      }
    }
    // Check if comment is updated (both original and edited have values AND they're different)
    else if (
      originalValue &&
      originalValue.trim().length > 0 &&
      editedValue &&
      editedValue.trim().length > 0 &&
      originalValue !== editedValue
    ) {
      const commentType = (commentTypeMap as any)?.[fieldName];
      const commentsArray =
        originalValues?.comments ||
        originalValues?.employeeApplicationComments ||
        [];
      const commentObj = (commentsArray || []).find(
        (c: any) => c && String(c.commentDateType) === String(commentType),
      );

      if (
        commentObj?.employeeApplicationCommentId &&
        !deletedCommentIds.includes(commentObj.employeeApplicationCommentId)
      ) {
        updatedComments.push({
          commentId: commentObj.employeeApplicationCommentId,
          comments: editedValue,
          commentType: commentType,
        });
      }
    }
  });

  // Build payload structure
  if (Object.keys(employeeInfoChanges).length > 0) {
    payload.employeeInfo = employeeInfoChanges;
  }
  if (Object.keys(schoolInfoChanges).length > 0) {
    payload.schoolInfo = schoolInfoChanges;
  }

  // Add update section for modified courses
  if (updatedCourses.length > 0) {
    if (!payload.update) {
      payload.update = {};
    }
    payload.update.courseList = updatedCourses;
  }

  // Include updated comments in update section if present
  if (updatedComments.length > 0) {
    if (!payload.update) {
      payload.update = {};
    }
    payload.update.commentsList = updatedComments;
  }

  // Add insert section for new courses and new comments
  if (newCourses.length > 0 || newComments.length > 0) {
    payload.insert = {};
    if (newCourses.length > 0) {
      payload.insert.courseList = newCourses;
    }
    if (newComments.length > 0) {
      payload.insert.commentsList = newComments;
    }
  }

  // Add delete section if there are deleted courses or comments
  if (deletedCourseIds.length > 0 || deletedCommentIds.length > 0) {
    payload.delete = {
      courseListIds: deletedCourseIds,
      commentsListIds: deletedCommentIds,
    };
  }

  // Add recursive sanitizer to convert empty strings to null in payload
  const sanitizeEmptyStrings = (value: any): any => {
    if (value === "") return null;
    if (Array.isArray(value)) return value.map(sanitizeEmptyStrings);
    if (value && typeof value === "object") {
      const result: Record<string, any> = {};
      Object.keys(value).forEach((key) => {
        result[key] = sanitizeEmptyStrings(value[key]);
      });
      return result;
    }
    return value;
  };

  // Sanitize payload so API receives nulls instead of empty strings
  return sanitizeEmptyStrings(payload);
};

/**
 * Merges editedValues and displayValues, handling nested structures
 * Returns flat field names for Form component
 */
const getMergedFormValues = (
  displayVals: Record<string, any>,
  editedVals: Record<string, any>,
): Record<string, any> => {
  // Start with flattened displayValues
  const flattened = flattenApplicationData(displayVals);

  // Merge with editedValues, flattening any nested structures
  const merged = { ...flattened, ...flattenApplicationData(editedVals) };

  return merged;
};

// Helper: detect if editedValues indicate explicit selection of the "--select--" placeholder for applicationTerm
const detectForceNullApplicationTerm = (
  editedValues: Record<string, any>,
): boolean => {
  console.log("editedValues in detectForceNullApplicationTerm:", editedValues);
  if (!editedValues || typeof editedValues !== "object") return false;
  if (Object.hasOwn(editedValues, "schoolInfo")) {
    if (editedValues.schoolInfo?.applicationTerm === null) {
      return true;
    }
  }
  // Check top-level applicationTerm as some flows may set it there
  if (Object.hasOwn(editedValues, "applicationTerm")) {
    if (editedValues.schoolInfo?.applicationTerm === null) return true;
  }
  return false;
};

// Helper: detect if editedValues indicate explicit clearing of preferred email (empPrefEmail)
const detectForceNullEmpPrefEmail = (
  editedValues: Record<string, any>,
  originalValues: Record<string, any> | undefined,
): boolean => {
  if (!editedValues || typeof editedValues !== "object") return false;
  const isPlaceholder = (val: any) =>
    val === null ||
    val === "" ||
    val === "--select--" ||
    String(val).trim() === "-100";
  if (
    editedValues.employeeInfo &&
    Object.hasOwn(editedValues.employeeInfo, "prefEmail")
  ) {
    const newVal = editedValues.employeeInfo.prefEmail;
    const origVal = originalValues?.employeeInfo?.prefEmail;
    return newVal !== origVal && isPlaceholder(newVal);
  }

  if (Object.hasOwn(editedValues, "empPrefEmail")) {
    const newVal = editedValues.empPrefEmail;
    const origVal = originalValues?.empPrefEmail;
    return newVal !== origVal && isPlaceholder(newVal);
  }

  return false;
};

interface AdminApplicationsDetailProps {
  applicationData: any;
}

const AllApplicationDetails: FC<AdminApplicationsDetailProps> = ({
  applicationData,
}) => {
  // Reset edit mode and edited values when applicationId changes
  useEffect(() => {
    setIsEditMode(false);
    setEditedValues({});
    setDeletedCourseIds([]);
    setDeletedCommentIds([]);
    setValidationErrors({});
  }, [applicationData?.applicationId]);
  const dispatch = useDispatch();
  const { applicationDetails } = useSelector(
    (state: RootState) => state.tuitionAdmin,
  );
  const { dropdownOptions } = useSelector(
    (state: RootState) => state.tuitionApply,
  );
  const errorBannerRef = useRef<HTMLDivElement>(null); // Ref to error banner for scrolling
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [isApplicationDetailsLoading, setIsApplicationDetailsLoading] =
    useState(false);
  const [applicationError, setApplicationError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [deletedCourseIds, setDeletedCourseIds] = useState<
    Array<string | number>
  >([]); // Track deleted course IDs
  const [deletedCommentIds, setDeletedCommentIds] = useState<number[]>([]); // Track deleted comment IDs
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({}); // Track validation errors
  const [shouldScrollToErrors, setShouldScrollToErrors] = useState(false); // Trigger scroll to error banner
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  // Fetch application details when applicationData changes
  useEffect(() => {
    if (!applicationData?.applicationId) {
      return;
    }

    const applicationId = applicationData.applicationId;
    const empNuid = applicationData.employeeNuId;

    setCurrentRequestId(applicationId);
    setIsApplicationDetailsLoading(true);
    setApplicationError(null);
    dispatch(
      fetchAllApplicationDetails({ applicationId, nuid: empNuid }) as any,
    )
      .then((result: any) => {
        if (result.type.endsWith("/rejected")) {
          setApplicationError(result.payload as string);
        }
      })
      .finally(() => {
        setIsApplicationDetailsLoading(false);
      });
  }, [applicationData, dispatch]);

  // Fetch dropdown options for all dropdown fields used in the form
  useEffect(() => {
    if (Object.keys(dropdownOptions).length === 0) {
      dispatch(
        fetchDropdownOptions({ fieldName: "all", applType: "AT001" }) as any,
      );
    }
  }, [dispatch, dropdownOptions]);

  // Scroll to error banner when validation errors appear and shouldScrollToErrors is true
  useEffect(() => {
    if (shouldScrollToErrors && Object.keys(validationErrors).length > 0) {
      // Use setTimeout to ensure DOM has rendered
      setTimeout(() => {
        // Scroll to error banner
        if (errorBannerRef.current) {
          errorBannerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        // Reset the scroll trigger
        setShouldScrollToErrors(false);
      }, 100);
    }
  }, [shouldScrollToErrors, validationErrors]);

  // Only show error if it's for the current request we're trying to load and not loading
  const isCurrentRequestError =
    currentRequestId === applicationData?.applicationId &&
    applicationError &&
    !isApplicationDetailsLoading;

  // Memoize mergedFormData to prevent unnecessary recalculations
  // Flatten nested employeeInfo and schoolInfo into form field names
  const mergedFormData = useMemo(() => {
    if (!applicationDetails) return {};
    const flattened = flattenApplicationData(applicationDetails);
    return flattened;
  }, [applicationDetails]);

  // Map incoming comment array to form fields using commentTypeMap
  const commentsArray =
    applicationDetails?.comments ||
    applicationDetails?.employeeApplicationComments ||
    [];

  const mappedComments = Object.entries(commentTypeMap || {}).reduce(
    (acc: Record<string, any>, [fieldName, commentType]) => {
      const foundValue = (commentsArray || []).find(
        (c: any) => c && String(c.commentDateType) === String(commentType),
      );
      if (foundValue?.comments !== undefined)
        acc[fieldName] = foundValue.comments;
      return acc;
    },
    {},
  );

  // Memoize displayValues to prevent unnecessary Form re-renders and ensure Form gets updated values
  const displayValues = useMemo((): Record<string, any> => {
    if (!applicationDetails) {
      return {};
    }

    const result = {
      ...mergedFormData,
      ...mappedComments,
      empShift: applicationDetails?.shiftStdHours ?? "",
    };
    return result;
  }, [applicationDetails, mergedFormData, mappedComments]);

  // Memoized initial values for the "At submission employment information" form
  const submittedEmploymentInitialValues = useMemo(() => {
    if (applicationDetails?.submittedEmplInfo) {
      return {
        ...applicationDetails.submittedEmplInfo,
        empHireDate: formatDateYMDToMDY(
          applicationDetails.submittedEmplInfo.empHireDate,
        ),
        empRehireDate: formatDateYMDToMDY(
          applicationDetails.submittedEmplInfo.empRehireDate,
        ),
      };
    }
 
    return displayValues || {};
  }, [applicationDetails?.submittedEmplInfo, displayValues]);
  // Get courses array from editedValues in edit mode, otherwise from applicationDetails - memoized to prevent unnecessary re-renders
  const courses = useMemo(
    () =>
      isEditMode
        ? editedValues?.courses || applicationDetails?.courses || []
        : applicationDetails?.courses || [],
    [isEditMode, editedValues?.courses, applicationDetails?.courses],
  );

  // Helper to get field value in edit mode or display mode
  const getCourseFieldValue = (
    fieldName: string,
    course: any,
    index: number,
  ) => {
    // Always use originZipCode/destinationZipCode for zip code fields
    if (fieldName === "originZip") {
      return editedValues?.courses?.[index]?.originZipCode ?? course?.originZip;
    }
    if (fieldName === "destinationZip") {
      return (
        editedValues?.courses?.[index]?.destinationZipCode ??
        course?.destinationZip
      );
    }
    if (isEditMode) {
      return editedValues?.courses?.[index]?.[fieldName] ?? course?.[fieldName];
    }

    return course?.[fieldName];
  };

  // Helper to get field value with display mapping
  const getCourseFieldWithDisplay = (
    fieldName: string,
    course: any,
    index: number,
  ) => {
    const value = getCourseFieldValue(fieldName, course, index);
    return isEditMode ? value : getDisplayValue(fieldName, value);
  };

  // Helper to build course base initial values
  const buildCourseBaseInitialValues = (course: any, index: number) => {
    const edited = isEditMode ? editedValues?.courses?.[index] : {};
    const startDateValue = getCourseFieldValue(
      "courseStartDate",
      course,
      index,
    );
    const endDateValue = getCourseFieldValue("courseEndDate", course, index);

    return {
      ...course,
      ...edited,
      courseStartDate: formatDateYMDToMDY(startDateValue),
      courseEndDate: formatDateYMDToMDY(endDateValue),
      totalEstimatedCourseFees: calculateCourseTotalFees(course),
      classSchoolName: getCourseFieldValue("schoolName", course, index) || "",
      courseProgramType:
        getCourseFieldValue("programType", course, index) || "",
      originZipCode: getCourseFieldValue("originZip", course, index) || "",
      destinationZipCode:
        getCourseFieldValue("destinationZip", course, index) || "",
      reimbursementType: getCourseFieldWithDisplay(
        "reimbursementType",
        course,
        index,
      ),
      originState: getCourseFieldWithDisplay("originState", course, index),
      destinationState: getCourseFieldWithDisplay(
        "destinationState",
        course,
        index,
      ),
    };
  };

  // Helper to build travel/mileage initial values
  const buildTravelInitialValues = (course: any, index: number) => {
    const edited = isEditMode ? editedValues?.courses?.[index] : {};
    const startDateValue = getCourseFieldValue(
      "courseStartDate",
      course,
      index,
    );
    const endDateValue = getCourseFieldValue("courseEndDate", course, index);

    return {
      ...course,
      ...edited,
      originState: getCourseFieldWithDisplay("originState", course, index),
      destinationState: getCourseFieldWithDisplay(
        "destinationState",
        course,
        index,
      ),
      originZipCode: getCourseFieldValue("originZip", course, index) || "",
      destinationZipCode:
        getCourseFieldValue("destinationZip", course, index) || "",
      courseStartDate: formatDateYMDToMDY(startDateValue),
      courseEndDate: formatDateYMDToMDY(endDateValue),
    };
  };

  // Helper to check if course is CEU type
  const isCEUCourse = (course: any, index: number): boolean => {
    const currentReimbursementType = getCourseFieldValue(
      "reimbursementType",
      course,
      index,
    );
    const displayValue = getDisplayValue(
      "reimbursementType",
      currentReimbursementType,
    );
    return (
      String(currentReimbursementType) === "1" ||
      String(currentReimbursementType) === "CEU" ||
      displayValue === "CEU"
    );
  };

  // Helper to get course fields for current mode
  const getCourseFields = (fieldGroupType?: string) => {
    const fields = isEditMode
      ? editableFieldsPerStep.step5
      : lockedFieldsPerStep.step5;
    if (!fieldGroupType) {
      return fields.filter((field: any) => !field.fieldGroupType);
    }
    return fields.filter(
      (field: any) => field.fieldGroupType === fieldGroupType,
    );
  };

  // Helper to filter validation errors for a specific course index
  const getCourseValidationErrors = (
    courseIndex: number,
  ): Record<string, string> => {
    if (courseIndex === 0) {
      // For first class, use both unprefixed errors and _class0 errors
      const courseErrors: Record<string, string> = {};
      Object.entries(validationErrors).forEach(([key, value]) => {
        if (!key.includes("_class")) {
          courseErrors[key] = value;
        } else if (key.endsWith("_class0")) {
          const fieldName = key.replace("_class0", "");
          courseErrors[fieldName] = value;
        }
      });
      return courseErrors;
    } else {
      // For other classes, filter by _class{index} suffix
      const suffix = `_class${courseIndex}`;
      const courseErrors: Record<string, string> = {};
      Object.entries(validationErrors).forEach(([key, value]) => {
        if (key.endsWith(suffix)) {
          const fieldName = key.replace(suffix, "");
          courseErrors[fieldName] = value;
        }
      });
      return courseErrors;
    }
  };

  // Helper to create value change handler for course
  const createCourseValueChangeHandler = (index: number) => {
    if (!isEditMode) return undefined;
    return (fieldName: string, value: any) =>
      handleValueChange(fieldName, value, index);
  };

  // Create a blur handler for course fields (only fetch miles data on blur, not on every keystroke)
  const createCourseBlurHandler = (index: number) => {
    if (!isEditMode) return undefined;
    return (fieldName: string, value: any) =>
      handleFieldBlur(fieldName, value, index);
  };

  // Handle edit mode toggle
  const handleEditMode = () => {
    setIsEditMode(true);
    // Initialize editedValues with flattened displayValues
    const flattenedValues = flattenApplicationData(displayValues);
    setEditedValues({
      ...flattenedValues,
      ...applicationDetails, // Preserve raw data like courses, comments, etc.
    });
    // Reset deleted courses when entering edit mode
    setDeletedCourseIds([]);
    // Reset deleted comments when entering edit mode
    setDeletedCommentIds([]);
    // Clear validation errors when entering edit mode
    setValidationErrors({});
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedValues({});
    setDeletedCourseIds([]);
    setDeletedCommentIds([]);
    setValidationErrors({});
  };

  // Handle comment field deletion/restoration
  const handleCommentFieldChange = (fieldName: string, value: any) => {
    const commentFields = [
      "employeeComments",
      "employmentJobComments",
      "noEstimatedFees",
    ];
    if (!commentFields.includes(fieldName)) return;

    const originalCommentValue = displayValues?.[fieldName];
    const isCommentBeingCleared =
      originalCommentValue &&
      originalCommentValue.trim().length > 0 &&
      (!value || value.trim().length === 0);
    const isCommentBeingRestored =
      value && value.trim().length > 0 && !originalCommentValue;

    if (!isCommentBeingCleared && !isCommentBeingRestored) return;

    const commentType = (commentTypeMap as any)?.[fieldName];
    const commentObj = (commentsArray || []).find(
      (c: any) => c && String(c.commentDateType) === String(commentType),
    );

    if (!commentObj?.employeeApplicationCommentId) return;

    if (isCommentBeingCleared) {
      setDeletedCommentIds((prev) =>
        prev.includes(commentObj.employeeApplicationCommentId)
          ? prev
          : [...prev, commentObj.employeeApplicationCommentId],
      );
    } else if (isCommentBeingRestored) {
      setDeletedCommentIds((prev) =>
        prev.filter((id) => id !== commentObj.employeeApplicationCommentId),
      );
    }
  };

  // Clear validation errors for a field
  const clearFieldValidationErrors = (
    fieldName: string,
    courseIndex?: number,
  ) => {
    setValidationErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[fieldName];
      if (courseIndex !== undefined) {
        delete newErrors[`${fieldName}_class${courseIndex}`];
      }
      return newErrors;
    });
  };

  // Build updater function for setting edited values
  const buildEditedValuesUpdater = (
    fieldName: string,
    value: any,
    courseIndex?: number,
  ) => {
    const mapping = FIELD_MAPPING[fieldName as keyof typeof FIELD_MAPPING];

    if (mapping) {
      return (prevValues: Record<string, any>) => ({
        ...prevValues,
        [mapping.section]: {
          ...prevValues?.[mapping.section],
          [mapping.key]: value,
        },
      });
    }

    const courseFields = [
      "courseName",
      "courseDeliveryType",
      "courseStartDate",
      "courseEndDate",
      "courseCredit",
      "estimatedTuitionFee",
      "estimatedBooksFee",
      "estimatedLabFee",
      "estimatedRegistrationFee",
      "estimatedOtherFee",
      "estimatedAirfareBaggageFee",
      "estimatedAutoRentalFee",
      "estimatedHotelFee",
      "estimatedParkTollFee",
      "estimatedPublicTransportFee",
      "totalNumberOfMiles",
      "estimatedMilesDollarValue",
      "originAddress",
      "originCity",
      "originState",
      "originZip",
      "destinationAddress",
      "destinationCity",
      "destinationState",
      "destinationZip",
      "numberOfTravelDays",
      "reimbursementType",
      "classSchoolName",
      "courseProgramType",
      "originZipCode",
      "destinationZipCode",
    ];

    if (courseFields.includes(fieldName)) {
      const fieldNameMap: Record<string, string> = {
        courseProgramType: "programType",
        classSchoolName: "schoolName",
        originZipCode: "originZipCode",
        destinationZipCode: "destinationZipCode",
      };
      const courseFieldName = fieldNameMap[fieldName] || fieldName;

      return (prevValues: Record<string, any>) => {
        const courses = prevValues?.courses || [];
        if (courses.length === 0) return { ...prevValues, [fieldName]: value };

        // use nullish coalescing operator instead of ternary for clarity
        const targetIndex = courseIndex ?? courses.length - 1;
        const updatedCourses = [...courses];
        updatedCourses[targetIndex] = {
          ...updatedCourses[targetIndex],
          [courseFieldName]: value,
        };
        return { ...prevValues, courses: updatedCourses };
      };
    }

    return (prevValues: Record<string, any>) => ({
      ...prevValues,
      [fieldName]: value,
    });
  };

  // Get course index from field name
  const getCourseIndexFromFieldName = (
    fieldName: string,
    courseIndex?: number,
  ): number => {
    if (courseIndex !== undefined) return courseIndex;
    if (!fieldName.includes("_class")) return 0;
    const regex = /_class(\d+)/;
    const match = regex.exec(fieldName);
    return match ? Number.parseInt(match[1]) : 0;
  };

  // Check if field should trigger miles API call
  const shouldFetchMilesData = (fieldName: string): boolean => {
    return (
      fieldName === "totalNumberOfMiles" ||
      fieldName === "courseStartDate" ||
      fieldName.startsWith("totalNumberOfMiles_class") ||
      fieldName.startsWith("courseStartDate_class")
    );
  };

  // Fetch and update miles data
  const fetchAndUpdateMilesData = (
    fieldName: string,
    value: any,
    courseIndex?: number,
    showAlerts: boolean = false,
  ) => {
    const index = getCourseIndexFromFieldName(fieldName, courseIndex);
    const editedCourse = editedValues?.courses?.[index];
    const displayCourse = displayValues?.courses?.[index];
    
    // Get courseStartDate - prioritize passed value, then edited, then display
    let courseStartDate = editedCourse?.courseStartDate ?? displayCourse?.courseStartDate;
    if (fieldName.startsWith("courseStartDate")) {
      courseStartDate = value;
    }
    
    // Get miles - prioritize passed value, then edited, then display
    let miles = editedCourse?.totalNumberOfMiles ?? displayCourse?.totalNumberOfMiles;
    if (fieldName.startsWith("totalNumberOfMiles")) {
      miles = value;
    }

    // Only courseStartDate is required before proceeding
    if (!courseStartDate) {
      return;
    }

    // Convert to MM/DD/YYYY format
    const dateFormatted = formatDateYMDToMDY(courseStartDate?.toString() || '');
    
    // Skip if couldn't convert date
    if (!dateFormatted) return;
    
    // Must have slashes to be a valid date format
    if (!dateFormatted.includes('/')) return;
    
    // Check if date has the basic format with slashes and 4-digit year
    const isCompleteFormat = /\/\d{4}$/.test(dateFormatted);
    
    if (!isCompleteFormat) {
      // Date has slashes but year is incomplete/invalid - show format alert only on blur
      if (showAlerts) {
        alert("Please enter a valid course start date (MM/DD/YYYY or M/D/YYYY format).");
      }
      return;
    }
    
    // Date looks complete, now validate month and day values
    if (!validateMonthDay(dateFormatted, showAlerts)) return;
    
    // Format with padding (MM-DD-YYYY) and call API
    let formattedDate = '';
    if (dateFormatted.includes("/")) {
      const [m, d, y] = dateFormatted.split("/");
      // Pad month and day to 2 digits
      const mm = m.padStart(2, "0");
      const dd = d.padStart(2, "0");
      formattedDate = `${mm}-${dd}-${y}`;
    }
    // Pass 0 if miles is empty, 0, or undefined
    const totalMiles = !miles || Number(miles) === 0 ? 0 : Number(miles);

    (
      dispatch(
        fetchMilesData({ courseStartDate: formattedDate, totalMiles }),
      ) as any
    )
      .then((result: any) => {
        if (result?.payload?.approvedMilesDollarValue !== undefined) {
          setEditedValues((prev) => {
            const updatedCourses = [...(prev?.courses || [])];
            updatedCourses[index] = {
              ...updatedCourses[index],
              estimatedMilesDollarValue:
                result.payload.approvedMilesDollarValue,
            };
            return { ...prev, courses: updatedCourses };
          });
        }
      })
      .catch(() => {});
  };

  // Handle value change in edit mode - tracks changes to both top-level and nested fields
  const handleValueChange = (
    fieldName: string,
    value: any,
    courseIndex?: number,
  ) => {
    clearFieldValidationErrors(fieldName, courseIndex);
    handleCommentFieldChange(fieldName, value);
    setEditedValues(buildEditedValuesUpdater(fieldName, value, courseIndex));
  };

  // Handle field blur - fetch miles data when user leaves either miles or date field
  const handleFieldBlur = (
    fieldName: string,
    value: any,
    courseIndex?: number,
  ) => {
    if (shouldFetchMilesData(fieldName)) {
      // Pass showAlerts: true to only show validation alerts on blur, not during typing
      fetchAndUpdateMilesData(fieldName, value, courseIndex, true);
    }
  };

  // Helper: Check if any fees were entered in courses
  const hasAnyFeesEntered = (): boolean => {
    const feeFields = [
      "estimatedTuitionFee",
      "estimatedRegistrationFee",
      "estimatedBooksFee",
      "estimatedLabFee",
      "estimatedOtherFee",
      "estimatedAirfareBaggageFee",
      "estimatedParkTollFee",
      "estimatedAutoRentalFee",
      "estimatedPublicTransportFee",
      "estimatedHotelFee",
      "estimatedMilesDollarValue",
      "totalNumberOfMiles",
    ];

    const parseNumberValue = (v: any): number => {
      if (v === undefined || v === null || v === "") return 0;
      if (typeof v === "number") return Number.isFinite(v) ? v : 0;
      const cleaned = String(v).replaceAll(/[^\d.-]/g, "");
      const num = Number(cleaned);
      return Number.isFinite(num) ? num : 0;
    };

    // Treat edited value as authoritative only when it's meaningful (not undefined/null/empty string)
    const effectiveValue = (edited: any, display: any) => {
      if (edited === undefined || edited === null || edited === "")
        return display;
      return edited;
    };

    // Check root-level fees in editedValues and displayValues
    const hasRootLevelFees = feeFields.some((fieldName) => {
      const editedValue = editedValues?.[fieldName];
      const displayValue = displayValues?.[fieldName];
      const value = effectiveValue(editedValue, displayValue);
      return parseNumberValue(value) > 0;
    });

    if (hasRootLevelFees) return true;

    // Check fees within courses (use edited courses if present and non-empty, otherwise display courses)
    const editedCourses =
      Array.isArray(editedValues?.courses) && editedValues.courses.length > 0
        ? editedValues.courses
        : undefined;
    const displayCourses = Array.isArray(displayValues?.courses)
      ? displayValues.courses
      : [];
    const allCourses = editedCourses ?? displayCourses;

    if (!allCourses || allCourses.length === 0) return false;

    const hasCourseFees = allCourses.some(
      (course: any, courseIndex: number) => {
        return feeFields.some((fieldName) => {
          const editedCourseVal =
            editedValues?.courses?.[courseIndex]?.[fieldName];
          const displayCourseVal = course?.[fieldName];
          const value = effectiveValue(editedCourseVal, displayCourseVal);
          return parseNumberValue(value) > 0;
        });
      },
    );

    return hasCourseFees;
  };

  // Validate that "No Estimated Fees" comment is provided if no CEU fees are entered
  const validateNoEstimatedFeesComment = (): boolean => {
    // If fees were entered, no need to validate this field and clear any existing related error
    if (hasAnyFeesEntered()) {
      setValidationErrors((prev) => {
        const { noEstimatedFees, ...rest } = prev;
        return rest;
      });
      return true;
    }

    // If no fees entered, check if "No Estimated Fees" comment is provided and meets length requirements
    const noFeesComment =
      (editedValues?.noEstimatedFees ?? displayValues?.noEstimatedFees) || "";
    const trimmed = String(noFeesComment).trim();

    // If empty, set an explicit error message
    if (trimmed.length === 0) {
      setValidationErrors((prev) => ({
        ...prev,
        noEstimatedFees:
          "Please explain why you are unable to estimate the costs.",
      }));
      return false;
    }

    // Clear any existing noEstimatedFees error and return valid
    setValidationErrors((prev) => {
      const { noEstimatedFees, ...rest } = prev;
      return rest;
    });
    return true;
  };

  // Validate form fields similar to apply flow
  const validateFormData = (): boolean => {
    const mergedValues = getMergedFormValues(displayValues, editedValues);
    const allErrors: Record<string, string> = {};

    // Convert dates from MM-DD-YYYY or ISO YYYY-MM-DD to MM/DD/YYYY for validation
    // Validation engine expects MM/DD/YYYY format
    const convertDatesToValidationFormat = (
      data: Record<string, any>,
    ): Record<string, any> => {
      const converted = { ...data };

      // Convert ANY field that looks like a date (MM-DD-YYYY or YYYY-MM-DD pattern)
      Object.keys(converted).forEach((field) => {
        const value = converted[field];
        if (value && typeof value === "string") {
          // Match MM-DD-YYYY pattern (with optional leading zeros)
          const mmddyyyyPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
          if (mmddyyyyPattern.test(value)) {
            converted[field] = value.split("-").join("/");
          }

          // Match ISO YYYY-MM-DD pattern
          const isoPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
          if (isoPattern.test(value)) {
            const [year, month, day] = value.split("-");
            const mmddyyyy = `${month}/${day}/${year}`;
            converted[field] = mmddyyyy;
          }
        }
      });

      return converted;
    };

    const validationData = convertDatesToValidationFormat(mergedValues);

    // Validate personal info fields
    const personalResult = validateTuitionForm(
      personalInfoFields,
      validationData,
    );
    if (!personalResult.isValid) {
      Object.assign(allErrors, personalResult.errors);
    }

    // Validate approver info fields (including manager and alternate manager phone numbers)
    const approverResult = validateTuitionForm(
      approverInfoFields,
      validationData,
    );
    if (!approverResult.isValid) {
      Object.assign(allErrors, approverResult.errors);
    }

    // Validate school/vendor info fields
    const schoolResult = validateTuitionForm(vendorInfoFields, validationData);
    if (!schoolResult.isValid) {
      Object.assign(allErrors, schoolResult.errors);
    }

    // Helper to map validation with class suffix
    const mapValidationWithClassSuffix = (
      validation: any,
      courseIndex: number,
    ) => {
      if (!validation.compareField) return validation;
      return {
        ...validation,
        compareField: `${validation.compareField}_class${courseIndex}`,
      };
    };

    // Helper to map course field with class suffix
    const mapCourseFieldWithClassSuffix = (field: any, courseIndex: number) => {
      if (courseIndex === 0) return field;
      return {
        ...field,
        name: `${field.name}_class${courseIndex}`,
        validations: field.validations?.map((validation: any) =>
          mapValidationWithClassSuffix(validation, courseIndex),
        ),
      };
    };

    // Helper to resolve API field name from a base field name avoiding nested callbacks
    const findApiFieldName = (
      baseFieldName: string,
      map: Record<string, string>,
    ) => {
      for (const key of Object.keys(map)) {
        if (map[key] === baseFieldName) return key;
      }
      return baseFieldName;
    };

    // Helper to convert date value to validation format
    const convertDateValueToValidationFormat = (fieldValue: string): string => {
      const mmddyyyyPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
      if (mmddyyyyPattern.test(fieldValue)) {
        return fieldValue.split("-").join("/");
      }

      const isoPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
      if (isoPattern.test(fieldValue)) {
        const [year, month, day] = fieldValue.split("-");
        return `${month}/${day}/${year}`;
      }

      return fieldValue;
    };

    // eslint-disable-next-line max-nested-callbacks
    // Helper to build course data for validation
    const buildCourseDataForValidation = (
      course: any,
      courseFields: any[],
      courseIndex: number,
    ) => {
      const courseData: Record<string, any> = {};
      const courseFieldMap: Record<string, string> = {
        programType: "courseProgramType",
        courseStartDate: "courseStartDate",
        courseEndDate: "courseEndDate",
        courseName: "courseName",
      };

      courseFields.forEach((field) => {
        const baseFieldName = field.name.replace(`_class${courseIndex}`, "");
        const apiFieldName = findApiFieldName(baseFieldName, courseFieldMap);

        let fieldValue = course[apiFieldName];

        if (fieldValue && typeof fieldValue === "string") {
          fieldValue = convertDateValueToValidationFormat(fieldValue);
        }

        const fieldKey =
          courseIndex === 0
            ? baseFieldName
            : `${baseFieldName}_class${courseIndex}`;
        courseData[fieldKey] = fieldValue;
      });

      return courseData;
    };

    // Validate each course
    const displayCourses = displayValues?.courses || [];
    const editedCourses = editedValues?.courses || [];
    const coursesData =
      editedCourses.length > 0 ? editedCourses : displayCourses;

    if (coursesData.length > 0) {
      coursesData.forEach((course: any, courseIndex: number) => {
        const courseFields = courseInfoFields.map((field) =>
          mapCourseFieldWithClassSuffix(field, courseIndex),
        );

        const courseData = buildCourseDataForValidation(
          course,
          courseFields,
          courseIndex,
        );
        const courseResult = validateTuitionForm(courseFields, courseData);

        if (!courseResult.isValid) {
          Object.assign(allErrors, courseResult.errors);
        }
      });
    }

    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      return false;
    }

    setValidationErrors({});
    return true;
  };

  // Handle save changes - builds payload with only changed values and calls API
  const handleSaveChanges = async () => {
    console.log("📊 Analytics: Save application changes clicked");
    trackButtonClick("Save changes", "tuition-admin-all-applications-details");
    // Validate form data
    if (!validateFormData()) {
      // Trigger scroll to error banner
      setShouldScrollToErrors(true);
      trackFormError("tuition", ["formValidation"]);
      return;
    }

    // Validate required fields
    if (!validateNoEstimatedFeesComment()) {
      // Validation function sets the appropriate error message; just trigger scroll to error banner
      setShouldScrollToErrors(true);
      trackFormError("tuition", ["estimatedFeesComment"]);
      return;
    }

    setIsSaving(true);
    trackFormSave("tuition", undefined, { applicationId: currentRequestId });

    // If user explicitly selected the "--select--" placeholder for applicationTerm,
    // include an explicit null in the payload so backend clears the value.
    const applicationTermEdited = !!(
      editedValues &&
      ((Object.hasOwn(editedValues, "schoolInfo") &&
        editedValues.schoolInfo &&
        Object.hasOwn(editedValues.schoolInfo, "applicationTerm")) ||
        Object.hasOwn(editedValues, "applicationTerm"))
    );

    const empPrefEmailEdited = !!(
      editedValues &&
      (Object.hasOwn(editedValues, "empPrefEmail") ||
        (editedValues.employeeInfo &&
          Object.hasOwn(editedValues.employeeInfo, "prefEmail")))
    );

    // Build payload with only changed values in proper nested structure
    const payload = buildPayload(
      editedValues,
      displayValues,
      deletedCourseIds,
      deletedCommentIds,
    );

    if (applicationTermEdited) {
      const shouldForceNullApplicationTerm =
        detectForceNullApplicationTerm(editedValues);
      if (shouldForceNullApplicationTerm) {
        payload.schoolInfo = payload.schoolInfo || {};
        payload.schoolInfo.applicationTerm = null;
      }
    }

    // Detect if user explicitly cleared preferred email
    if (empPrefEmailEdited) {
      const shouldForceNullEmpPrefEmail = detectForceNullEmpPrefEmail(
        editedValues,
        displayValues,
      );

      // Ensure explicit null for empPrefEmail only when user explicitly cleared/selected placeholder
      if (shouldForceNullEmpPrefEmail) {
        payload.employeeInfo = payload.employeeInfo || {};
        payload.employeeInfo.prefEmail = null;
      }
    }

    // Call updateApplicationDetails to save the changes
    dispatch(
      updateAdminApplicationDetails({
        applicationId: currentRequestId || "",
        data: payload,
        nuid: applicationData?.employeeNuId,
      }) as any,
    )
      .then((result: any) => {
        if (result.type.endsWith("/fulfilled")) {
          // After successful save, refetch the application details
          dispatch(
            fetchAllApplicationDetails({
              applicationId: currentRequestId || "",
              nuid: applicationData?.employeeNuId,
            }) as any,
          );
          // Exit edit mode and reset deleted courses and comments
          setIsEditMode(false);
          setEditedValues({});
          setDeletedCourseIds([]);
          setDeletedCommentIds([]);
          setValidationErrors({});
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  // Handle adding a new course/class
  const handleAddCourse = () => {
    const currentCourses =
      editedValues?.courses || applicationDetails?.courses || [];
    const newCourse = {
      id: `${Date.now()}-${currentCourses.length}`,
      courseName: "",
      courseStartDate: "",
      courseEndDate: "",
      courseCredit: "",
      schoolName: "",
      reimbursementType: "",
      programType: "",
      courseDeliveryType: "",
      estimatedTuitionFee: null,
      estimatedBooksFee: null,
      estimatedLabFee: null,
      estimatedRegistrationFee: null,
      estimatedOtherFee: null,
      estimatedAirfareBaggageFee: null,
      estimatedAutoRentalFee: null,
      estimatedHotelFee: null,
      estimatedParkTollFee: null,
      estimatedPublicTransportFee: null,
      totalNumberOfMiles: null,
      estimatedMilesDollarValue: null,
      originAddress: null,
      originCity: null,
      originState: null,
      originZip: null,
      destinationAddress: null,
      destinationCity: null,
      destinationState: null,
      destinationZip: null,
      numberOfTravelDays: null,
    };

    const updatedCourses = [...currentCourses, newCourse];
    setEditedValues((prev) => ({
      ...prev,
      courses: updatedCourses,
    }));
  };

  // Handle deleting a course/class
  const handleDeleteCourse = (courseIndex: number) => {
    const currentCourses =
      editedValues?.courses || applicationDetails?.courses || [];
    const courseToDelete = currentCourses[courseIndex];

    // Get the course ID (employeeCourseId or fallback to courseId or id)
    const courseId =
      courseToDelete?.employeeCourseId ??
      courseToDelete?.courseId ??
      courseToDelete?.id;

    // If we have any identifier for the course, always record it so the API can act on delete.
    // This may be a numeric DB id or a transient string id used for newly added local rows.
    if (courseId !== undefined && courseId !== null) {
      setDeletedCourseIds((prev) =>
        prev.includes(courseId) ? prev : [...prev, courseId],
      );
    }

    // Remove from edited courses
    const updatedCourses = currentCourses.filter(
      (_: any, index: number) => index !== courseIndex,
    );
    setEditedValues((prev) => ({
      ...prev,
      courses: updatedCourses,
    }));
    // Clear validation errors for the deleted course and reindex errors for remaining courses
    setValidationErrors((prevErrors) => {
      const newErrors: Record<string, string> = {};

      Object.entries(prevErrors).forEach(([key, value]) => {
        // Check if this is a course field error
        if (courseIndex === 0 && !key.includes("_class")) {
          // Skip errors for the first class being deleted (no suffix)
          return;
        } else if (key.endsWith(`_class${courseIndex}`)) {
          // Skip errors for the deleted class
          return;
        } else if (key.includes("_class")) {
          // Reindex errors for courses after the deleted one
          const execResult = /^(.+)_class(\d+)$/.exec(key);
          if (execResult) {
            const fieldName = execResult[1];
            const classIndex = Number.parseInt(execResult[2]);

            if (classIndex > courseIndex) {
              // Shift down the index for courses after the deleted one
              const newKey = `${fieldName}_class${classIndex - 1}`;
              newErrors[newKey] = value;
            } else if (classIndex < courseIndex) {
              // Keep errors for courses before the deleted one
              newErrors[key] = value;
            }
          }
        } else {
          // Keep non-course errors
          newErrors[key] = value;
        }
      });

      return newErrors;
    });
  };
  // Helper function to calculate total estimated fees for a specific course
  const calculateCourseTotalFees = (course: any) => {
    const feeFieldNames = [
      "estimatedTuitionFee",
      "estimatedBooksFee",
      "estimatedLabFee",
      "estimatedRegistrationFee",
      "estimatedOtherFee",
      "estimatedAirfareBaggageFee",
      "estimatedParkTollFee",
      "estimatedAutoRentalFee",
      "estimatedPublicTransportFee",
      "estimatedHotelFee",
      "estimatedMilesDollarValue",
    ];

    let totalFees = 0;
    feeFieldNames.forEach((fieldName) => {
      const value = Number(course[fieldName]) || 0;
      totalFees += value;
    });

    return totalFees.toFixed(2);
  };

  // Scroll to section function
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Helper function to get dropdown options for a field
  const getFieldDropdownOptions = (field: any): any[] | undefined => {
    if (
      field.name === "reimbursementType" &&
      dropdownOptions.reimbursement_type
    ) {
      return dropdownOptions.reimbursement_type.map((item: any) => ({
        value: item.value,
        label: item.value,
      }));
    }

    if (
      (field.name === "originState" || field.name === "destinationState") &&
      dropdownOptions.state
    ) {
      return dropdownOptions.state.map((item: any) => ({
        value: item.value,
        label: item.value,
      }));
    }

    return undefined;
  };

  /**
   * Retrieves display value from dropdown options or returns string code value
   */
  const getDropdownValue = (
    options: any[] | undefined,
    codeValue: string | number,
  ): string => {
    if (!options) return String(codeValue);
    const found = options.find(
      (item: any) => String(item.code) === String(codeValue),
    );
    return found?.value || String(codeValue);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const getDisplayValue = (
    fieldName: string,
    codeValue: string | number | undefined,
  ): string => {
    // Treat special sentinel value -100 for degreeCompletionYear as the "--select--" placeholder
    if (
      fieldName === "degreeCompletionYear" &&
      (codeValue === -100 || String(codeValue) === "-100")
    ) {
      return "Non Degree Program";
    }

    if (!codeValue && codeValue !== 0) return "";

    if (
      fieldName === "reimbursementType" &&
      dropdownOptions.reimbursement_type
    ) {
      return getDropdownValue(
        dropdownOptions.reimbursement_type,
        codeValue,
      );
    }

    if (
      (fieldName === "originState" || fieldName === "destinationState") &&
      dropdownOptions.state
    ) {
      return getDropdownValue(dropdownOptions.state, codeValue);
    }

    if (fieldName === "applicationTerm" && dropdownOptions.application_term) {
      return getDropdownValue(dropdownOptions.application_term, codeValue);
    }

    return String(codeValue);
  };

  const handlePrintFaxCover = useCallback(async () => {
    if (!applicationData?.applicationId) {
      console.error("No application id provided.");
      return;
    }

    setDownloadError(null);
    setIsPrinting(true);
    const action = await dispatch<any>(
      downloadPrintFaxCover({
        applicationId: applicationData?.applicationId,
        nuid: applicationData?.employeeNuId,
      }),
    );

    setIsPrinting(false);

    if (!downloadPrintFaxCover.fulfilled.match(action)) {
      setDownloadError(
        String(action.payload || "Failed to download fax cover sheet"),
      );
    }
  }, [applicationData?.applicationId, dispatch]);

  const handlePrintApplication = async () => {
    if (!applicationData?.applicationId) {
      console.error("No application id provided.");
      return;
    }
    setDownloadError(null);
    setIsPrinting(true);
    const action = await dispatch<any>(
      downloadPrintApplication({
        applicationId: applicationData?.applicationId,
        nuid: applicationData?.employeeNuId,
      }),
    );

    setIsPrinting(false);

    if (!downloadPrintApplication.fulfilled.match(action)) {
      setDownloadError(
        String(action.payload || "Failed to download application"),
      );
    }
  };
  // Lock fields (read-only)
  const createLockedFields = (fields: any[]) => {
    return fields.map((field) => {
      const fieldOptions = getFieldDropdownOptions(field);
      return {
        ...field,
        disabled: true,
        helperText: "Read only; this value is assigned automatically by HR",
        required: false,
        type: field.type === "select" ? "text" : field.type,
        placeholder: field.placeholder || "",
        isSearchBtnRequired:
          field.name === "alternateNuid" ? false : field.isSearchButtonRequired,
        // Add options if this is a select field with dropdown data
        ...(field.type === "select" && fieldOptions
          ? { options: fieldOptions }
          : {}),
      };
    });
  };

  // Get editable fields from config (unlock only fields not marked as disabled in config)
  const createEditableFields = (fields: any[]) => {
    return fields.map((field) => {
      const fieldOptions = getFieldDropdownOptions(field);
      // Only enable fields that are not marked as disabled in the original config
      const isConfigDisabled = field.disabled === true;
      // Force required: true for applicationYear in admin edit mode
      const isApplicationYear = field.name === "applicationYear";
      return {
        ...field,
        disabled: isConfigDisabled,
        helperText: isConfigDisabled ? field.helperText : undefined,
        required: isApplicationYear ? true : field.required || false,
        placeholder: field.placeholder || "",
        isSearchBtnRequired: field.isSearchButtonRequired || false,
        // Add options if this is a select field with dropdown data
        ...(field.type === "select" && fieldOptions
          ? { options: fieldOptions }
          : {}),
      };
    });
  };

  // Locked fields per step
  const lockedFieldsPerStep = Object.fromEntries(
    Object.entries(tuitionStepFields).map(([step, fields]: [string, any]) => [
      step,
      createLockedFields(
        (fields as any[]).filter(
          (field: any) =>
            !(step === "step3" && field.name === "alternateNuid") &&
            step !== "step7", // Exclude participant conditions entirely
        ),
      ),
    ]),
  );

  // Editable fields per step (for edit mode)
  const editableFieldsPerStep = Object.fromEntries(
    Object.entries(tuitionStepFields).map(([step, fields]: [string, any]) => [
      step,
      createEditableFields(
        (fields as any[]).filter(
          (field: any) =>
            !(step === "step3" && field.name === "alternateNuid") &&
            step !== "step7", // Exclude participant conditions entirely
        ),
      ),
    ]),
  );

  // Extract ternary for onValueChange handler
  const onValueChangeHandler = isEditMode ? handleValueChange : undefined;

  // Employment render content extracted outside the steps array
  const renderEmploymentContent: JSX.Element = (
    <div className="space-y-8">
      {/* Employment fields section */}
      <div>
        <Form
          fields={
            isEditMode
              ? editableFieldsPerStep.step2.filter(
                  (f: any) => f.fieldGroupType === "employmentInitial",
                )
              : lockedFieldsPerStep.step2.filter(
                  (f: any) => f.fieldGroupType === "employmentInitial",
                )
          }
          initialValues={(() => {
            const vals = isEditMode
              ? getMergedFormValues(displayValues, editedValues)
              : displayValues || {};

            return {
              ...vals,
              empHireDate: formatDateYMDToMDY(vals?.empHireDate),
              empRehireDate: formatDateYMDToMDY(vals?.empRehireDate),
            };
          })()}
          buttons={[]}
          isViewDetails={!isEditMode}
          onValueChange={onValueChangeHandler}
          onBlur={isEditMode ? handleFieldBlur : undefined}
        />
      </div>

      {/* At submission employment information section */}
      <div>
        <h4 className="h4 margin-bottom-2u">
          At submission employment information
        </h4>
        <Form
          fields={lockedFieldsPerStep.step2.filter(
            (f: any) =>
              f.fieldGroupType === "employmentInitial" &&
              [
                "empId",
                "regionId",
                "regionName",
                "unionId",
                "unionName",
                "jobTitleCode",
                "jobTitleName",
                "shiftStdHours",
                "empHireDate",
                "empRehireDate",
                "empJobStatus",
                "empType",
              ].includes(f.name),
          )}
          initialValues={submittedEmploymentInitialValues}
          buttons={[]}
          isViewDetails={true}
          onValueChange={undefined}
        />
      </div>

      {/* Manager information section (no divider) */}
      <div>
        <h4 className="h4 margin-bottom-2u">Manager information</h4>
        <p className="p3 -book mb-6!">
          The following Direct Manager has been identified for you from the HR
          My Org hierarchy. This manager will receive all standard emails and
          notifications of employee education benefit applications submitted
          here. If known, please provide the manager's work phone. If this
          manager information is incorrect, contact your direct manager or
          supervisor.
        </p>
        <Form
          fields={
            isEditMode
              ? editableFieldsPerStep.step3.filter(
                  (f: any) => f.fieldGroupType === "manager",
                )
              : lockedFieldsPerStep.step3.filter(
                  (f: any) => f.fieldGroupType === "manager",
                )
          }
          initialValues={
            isEditMode
              ? getMergedFormValues(displayValues, editedValues)
              : displayValues
          }
          buttons={[]}
          isViewDetails={!isEditMode}
          onValueChange={isEditMode ? handleValueChange : undefined}
          onBlur={isEditMode ? handleFieldBlur : undefined}
          externalValidationErrors={validationErrors}
        />
        <p className="p3 -book my-6!">
          If another manager or supervisor should ALSO receive standard emails
          for approvals and notifications of employee education benefit
          applications submitted here, you may add that manager or supervisor
          here by NUID. If known, please provide the manager's work phone.
          (Note:You may also add or edit that information by accessing the
          Employee information portlet (located in the upper right corner of the
          My Education Benefit Applications page) and update information for
          your Self-Reported Manager.)
        </p>
        <Form
          fields={
            isEditMode
              ? editableFieldsPerStep.step3
                  .filter((f: any) => f.fieldGroupType === "managerAlt")
                  .map((f: any) =>
                    f.name === "mgrNuid"
                      ? { ...f, name: "mgrNuid", label: "Manager NUID" }
                      : f,
                  )
              : lockedFieldsPerStep.step3
                  .filter((f: any) => f.fieldGroupType === "managerAlt")
                  .map((f: any) =>
                    f.name === "altNuid"
                      ? { ...f, name: "altNuid", label: "Alternative NUID" }
                      : f,
                  )
          }
          initialValues={
            isEditMode
              ? getMergedFormValues(displayValues, editedValues)
              : displayValues
          }
          buttons={[]}
          isViewDetails={!isEditMode}
          onValueChange={isEditMode ? handleValueChange : undefined}
          onBlur={isEditMode ? handleFieldBlur : undefined}
          externalValidationErrors={validationErrors}
        />
      </div>
    </div>
  );

  // Comments render content (localized here — does not change shared components)
  const renderCommentsContent: JSX.Element = (
    <div>
      {(() => {
        const comments = isEditMode
          ? editableFieldsPerStep.step6
          : lockedFieldsPerStep.step6 || [];
        const getFields = (name: string) =>
          comments.filter((f: any) => f.name === name);
        const hasFeesEntered = hasAnyFeesEntered();

        const commentSections = [
          {
            name: "employeeComments",
            title: "Employee comments",
            subtitle:
              "Briefly explain how this program will help develop your career at Kaiser Permanente.",
          },
          {
            name: "employmentJobComments",
            title: "Employment/Job comments",
            subtitle:
              "What job at do you plan to advance in with the completion of this program?",
          },
          {
            name: "noEstimatedFees",
            title: "No estimated fees",
            subtitle:
              "You have not entered an estimated fees or explain why you are unable to estimate the costs.",
          },
        ];

        // Check backend comments array by commentDateType (use commentTypeMap)
        const hasResponse = (fieldName: string) => {
          const expectedType = (commentTypeMap as any)?.[fieldName];
          if (!expectedType) return false;
          const found = (commentsArray || []).find(
            (c: any) => c && String(c.commentDateType) === String(expectedType),
          );
          return !!found && String(found.comments || "").trim().length > 0;
        };

        return (
          <div>
            {commentSections.map((section) => {
              // Show noEstimatedFees only when no fees are entered OR if it already has a response
              if (section.name === "noEstimatedFees") {
                // In edit mode: show if no fees entered
                // In view mode: show only if it has a response
                const shouldShowComment = isEditMode
                  ? !hasFeesEntered
                  : !hasFeesEntered && hasResponse(section.name);
                if (!shouldShowComment) return null;
              }

              const fields = getFields(section.name);
              return (
                <div className="mb-6" key={section.name}>
                  <h4 className="h4 margin-bottom-2u">{section.title}</h4>
                  {section.subtitle && (
                    <p className="p3 -book mb-4!">{section.subtitle}</p>
                  )}
                  <Form
                    fields={fields.map((f: any) => ({
                      ...f,
                      label: "Employee comments",
                    }))}
                    initialValues={
                      isEditMode
                        ? getMergedFormValues(displayValues, editedValues)
                        : displayValues
                    }
                    buttons={[]}
                    isViewDetails={!isEditMode}
                    onValueChange={isEditMode ? handleValueChange : undefined}
                    onBlur={isEditMode ? handleFieldBlur : undefined}
                  />
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );

  // Determine save button text based on saving state
  const saveButtonText = isSaving ? "Saving..." : "Save";

  // Helpers to compute updatedBy and updatedDate with optional txn prefix support
  const getUpdatedBy = (data: any, isTxn: boolean = false) => {
    if (!data) return undefined;
    const lastKey = isTxn ? "txnLastUpdatedBy" : "lastUpdatedBy";
    const createKey = isTxn ? "txnCreatedBy" : "createdBy";
    return data?.[lastKey] === null ? data?.[createKey] : data?.[lastKey];
  };

  const getUpdatedDate = (data: any, isTxn: boolean = false) => {
    if (!data) return undefined;
    const lastTimeKey = isTxn ? "txnLastUpdatedTime" : "lastUpdatedTime";
    const createTimeKey = isTxn ? "txnCreatedTime" : "createdTime";
    const time = data?.[lastTimeKey] === null ? data?.[createTimeKey] : data?.[lastTimeKey];
    return time ? formatTimestamp(time) : undefined;
  };

  // Helper to pick editable vs locked fields for a given step key
  const getStepFields = (stepKey: string): any[] => {
    const edited = (editableFieldsPerStep as Record<string, any>)[stepKey];
    const locked = (lockedFieldsPerStep as Record<string, any>)[stepKey];
    return isEditMode ? edited ?? [] : locked ?? [];
  };

  // Map step names to IDs and titles for buttons
  const steps = [
    {
      id: "applicant-information",
      title: "Applicant information",
      fields: getStepFields("step1"),
      updatedBy: getUpdatedBy(displayValues),
      updatedDate: getUpdatedDate(displayValues),
    },
    {
      id: "employment-information",
      title: "Employment information",
      subtitle: "Current employment information",
      renderContent: renderEmploymentContent,
      updatedBy: getUpdatedBy(displayValues, true),
      updatedDate: getUpdatedDate(displayValues, true),
    },
    {
      id: "school-information",
      title: "School information",
      fields: getStepFields("step4"),
      updatedBy: getUpdatedBy(applicationDetails?.schoolInfo),
      updatedDate: getUpdatedDate(applicationDetails?.schoolInfo),
    },
    {
      id: "class-information",
      title: "Class information",
      fields: [],
    },
    {
      id: "comments",
      title: "Comments",
      subtitle:
        "Travel is only reimbursed for continuing education courses and events. Select CEU to enter travel estimates.",
      fields: getStepFields("step6"),
      renderContent: renderCommentsContent,
      updatedBy: getUpdatedBy(commentsArray?.[0]),
      updatedDate: getUpdatedDate(commentsArray?.[0]),
    },
  ];

  // Render edit/save/cancel buttons based on mode
  const renderEditModeButtons = isEditMode ? (
    <>
      <button
        onClick={handleSaveChanges}
        disabled={
          isSaving || Object.keys(validationErrors).length > 0
        }
        className="button -primary"
      >
        {saveButtonText}
      </button>
      
      <button
        onClick={handleCancelEdit}
        disabled={isSaving}
        className="button -secondary"
      >
        Cancel
      </button>
      {isSaving && <Loader />}
    </>
  ) : (
    <button
      onClick={handleEditMode}
      className="button -primary"
    >
      Edit Application
    </button>
  );

  return (
    <div className="space-y-8">
      {/* Error State */}
      {isCurrentRequestError && (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="p-4  max-w-md text-center">
            <Notifications messageInfo={applicationError} status={false} />
          </div>
        </div>
      )}

      {!isCurrentRequestError && (
        <>
          {/* Loading State - show loader while fetching or waiting for data */}
          {(isApplicationDetailsLoading || !applicationDetails || isPrinting) && <Loader />}

          {/* Only render content after we have data */}
          {applicationDetails && (
            <>
              <h3 className="h3 mt-8! mb-4!">Jump to applicant section:</h3>
              {/* Navigation Buttons with Edit/Save/Cancel */}
              <div className="sticky top-0 bg-white z-10 pb-4 mb-4">
                <div className="flex gap-4 flex-wrap items-center p-5!">
                  {steps.map((step) => (
                    <button
                      key={step.id}
                      className="button -primary-inverted"
                      onClick={() => scrollToSection(step.id)}
                    >
                      {step.title}
                    </button>
                  ))}
                  {/* Edit/Save/Cancel Buttons in Top Row */}
                  {renderEditModeButtons}
                  <div className="flex gap-4 mb-4">
                    <button
                      className="button -primary-inverted"
                      onClick={handlePrintFaxCover}
                    >
                      Print fax cover sheet
                    </button>
                    <button
                      className="button -primary-inverted"
                      onClick={handlePrintApplication}
                    >
                      Print application
                    </button>
                  </div>
                </div>
              </div>
              {downloadError && (
                <UploadErrorBanner
                  message={
                    "There was an error downloading the document. Please try again later."
                  }
                  header={"Error"}
                />
              )}
              {/* Validation Errors Banner - Shown at Top */}
              {Object.keys(validationErrors).length > 0 && isEditMode && (
                <div ref={errorBannerRef} className="m-8!">
                  <AdminTuitionErrorBanner
                    currentStep={5}
                    errors={validationErrors}
                    fieldLabels={getFieldLabels()}
                    formData={getMergedFormValues(displayValues, editedValues)}
                  />
                </div>
              )}

              <p className="p3 -book mt-4!">
                All fields are optional unless marked as required. Users must
                fill out one field in order to proceed. Once data is inserted
                within the employeed id and nuid, the rest of the fields are
                auto-inputted.
              </p>

              {/* Sections */}
              {steps.map((step) => (
                <div key={step.id}>
                  <div id={step.id}>
                    {step.id !== "class-information" && (
                      <>
                        <h3 className="h3 margin-top-2u margin-bottom-2u">
                          {step.title}
                        </h3>
                        {step.subtitle && (
                          <h4 className="h4  mb-6!">{step.subtitle}</h4>
                        )}
                        <div className="flex gap-70 mb-3.5!">
                          <p className="p3 -book">
                            Updated by:{step.updatedBy}
                          </p>
                          <p className="p3 -book">
                            Updated date :{step.updatedDate}
                          </p>
                        </div>
                      </>
                    )}
                    {step.renderContent ?? (
                      <Form
                        key={`${step.id}-${isEditMode}`}
                        fields={step.fields}
                        initialValues={
                          step.id === "school-information"
                            ? (() => {
                                const mergedData = isEditMode
                                  ? getMergedFormValues(
                                      displayValues,
                                      editedValues,
                                    )
                                  : displayValues;
                                return {
                                  ...mergedData,
                                  applicationYear: mergedData?.applicationYear,
                                  applicationTerm: getDisplayValue(
                                    "applicationTerm",
                                    mergedData?.applicationTerm,
                                  ),
                                  termBegin: formatDateYMDToMDY(
                                    mergedData?.termBegin,
                                  ),
                                  termEnd: mergedData?.termEnd
                                    ? formatDateYMDToMDY(mergedData?.termEnd)
                                    : undefined,
                                  degreeCompletionYear:
                                    mergedData?.degreeCompletionYear === "-100"
                                      ? "Non Degree Program"
                                      : mergedData?.degreeCompletionYear,
                                };
                              })()
                            : (() => {
                                const formValues = isEditMode
                                  ? getMergedFormValues(
                                      displayValues,
                                      editedValues,
                                    )
                                  : displayValues;
                                return formValues;
                              })()
                        }
                        buttons={[]}
                        isViewDetails={!isEditMode}
                        onValueChange={
                          isEditMode ? handleValueChange : undefined
                        }
                        schoolNameSuggestionsEndpoint={
                          ADMIN_SCHOOL_NAME_SUGGESTIONS_ENDPOINT
                        }
                        externalValidationErrors={validationErrors}
                      />
                    )}
                    {step.id !== "class-information" && (
                      <hr className="-variation-1" />
                    )}
                  </div>

                  {/* Render Dynamic Class Information after School Information */}
                  {step.id === "class-information" &&
                    (isEditMode || courses.length >= 0) && (
                      <div id="class-information">
                        <h3 className="h3 margin-top-4u margin-bottom-4u">
                          Class information
                        </h3>

                        {/* Placeholder when no classes exist in edit mode */}
                        {isEditMode && (!courses || courses.length === 0) && (
                          <p className="p3 -book mb-4">No classes added yet</p>
                        )}
                        {!isEditMode && (!courses || courses.length === 0) && (
                          <p className="p3 -book mb-4">
                            No class information found to display
                          </p>
                        )}
                        {/* Render existing courses (if any) */}
                        {courses &&
                          courses.length > 0 &&
                          courses.map((course: any, index: number) => {
                            const isCEU = isCEUCourse(course, index);
                            const canDelete = isEditMode && courses.length > 1;

                            return (
                              <div
                                key={course.id ?? `course-${index}`}
                                id={`class-section-${index}`}
                                className="mb-8"
                              >
                                <h4 className="h4 margin-bottom-2u">
                                  Class {index + 1}
                                </h4>

                                {/* Per-course updated info */}
                                <div className="flex gap-70 mb-3.5!">
                                  <p className="p3 -book">
                                    Updated by:{" "}
                                    {course?.lastUpdatedBy === null
                                      ? course?.createdBy
                                      : course?.lastUpdatedBy}
                                  </p>
                                  <p className="p3 -book">
                                    Updated date:{" "}
                                    {course?.lastUpdatedTime === null
                                      ? formatTimestamp(course?.createdTime)
                                      : formatTimestamp(
                                          course?.lastUpdatedTime,
                                        )}
                                  </p>
                                </div>

                                {/* Render course base fields (excluding grouped fields) */}
                                <Form
                                  key={`course-${index}-${isEditMode}`}
                                  fields={getCourseFields()}
                                  initialValues={buildCourseBaseInitialValues(
                                    course,
                                    index,
                                  )}
                                  buttons={[]}
                                  isViewDetails={!isEditMode}
                                  schoolNameSuggestionsEndpoint={
                                    ADMIN_SCHOOL_NAME_SUGGESTIONS_ENDPOINT
                                  }
                                  onValueChange={createCourseValueChangeHandler(
                                    index,
                                  )}
                                  onBlur={createCourseBlurHandler(index)}
                                  externalValidationErrors={getCourseValidationErrors(
                                    index,
                                  )}
                                />

                                {/* Delete Class Button - show immediately after base fields */}
                                {canDelete && (
                                  <div className="flex justify-end mb-6 mt-6">
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleDeleteCourse(index);
                                      }}
                                      className="flex items-center gap-2 text-[20px] font-[350] text-(--Contrast-Blue,#0074AD) hover:underline"
                                      aria-label={`Delete class ${index + 1}`}
                                    >
                                      <img
                                        src="/assets/images/trash-solid.svg"
                                        alt="Delete"
                                        className="w-5 h-5 inline-block text-(--Contrast-Blue,#0074AD)"
                                      />
                                      <span className="text-[20px] font-[350] text-(--Contrast-Blue,#0074AD)">
                                        Delete class
                                      </span>
                                    </button>
                                  </div>
                                )}

                                {/* Render Travel Estimates Section if reimbursementType is CEU (code=1) */}
                                {isCEU && (
                                  <div className="mt-6">
                                    <h4 className="h4 margin-bottom-2u">
                                      Travel information - continuing education
                                      units only
                                    </h4>
                                    <p className="p3 -book mb-6!">
                                      You need to submit receipts for all travel
                                      expenses. Check your policy for travel
                                      limits.
                                    </p>
                                    <Form
                                      key={`travel-${index}-${isEditMode}`}
                                      fields={getCourseFields("travelRelated")}
                                      initialValues={buildTravelInitialValues(
                                        course,
                                        index,
                                      )}
                                      buttons={[]}
                                      isViewDetails={!isEditMode}
                                      schoolNameSuggestionsEndpoint={
                                        ADMIN_SCHOOL_NAME_SUGGESTIONS_ENDPOINT
                                      }
                                      onValueChange={createCourseValueChangeHandler(
                                        index,
                                      )}
                                      onBlur={createCourseBlurHandler(index)}
                                      externalValidationErrors={getCourseValidationErrors(
                                        index,
                                      )}
                                    />
                                  </div>
                                )}

                                {/* Render Mileage Section if reimbursementType is CEU (code=1) */}
                                {isCEU && (
                                  <div className="mt-6">
                                    <h4 className="h4 margin-bottom-2u">
                                      Total number of miles (round-trip)
                                    </h4>
                                    <p className="p3 -book mb-6!">
                                      Total dollar value will be calculated
                                      based on the IRS standard rate
                                    </p>
                                    <Form
                                      key={`mileage-${index}-${isEditMode}`}
                                      fields={getCourseFields("mileageRelated")}
                                      initialValues={buildTravelInitialValues(
                                        course,
                                        index,
                                      )}
                                      buttons={[]}
                                      isViewDetails={!isEditMode}
                                      schoolNameSuggestionsEndpoint={
                                        ADMIN_SCHOOL_NAME_SUGGESTIONS_ENDPOINT
                                      }
                                      onValueChange={createCourseValueChangeHandler(
                                        index,
                                      )}
                                      onBlur={createCourseBlurHandler(index)}
                                      externalValidationErrors={getCourseValidationErrors(
                                        index,
                                      )}
                                    />
                                  </div>
                                )}

                                {/* Delete Class Button - Only show in edit mode from course 2 onwards */}
                              </div>
                            );
                          })}

                        {/* Add Additional Class Button - show in edit mode even when no courses */}
                        {isEditMode && (
                          <div className="flex justify-start mt-6 mb-6">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handleAddCourse();
                              }}
                              className="flex items-center gap-2 text-[20px] font-[350] text-(--Contrast-Blue,#0074AD) hover:underline justify-start"
                              style={{ justifyContent: "flex-start" }}
                              aria-label="Add additional class"
                            >
                              <img
                                src="/assets/images/plus-outline.svg"
                                alt="Add"
                                className="w-5 h-5 inline-block text-(--Contrast-Blue,#0074AD)"
                              />
                              <span className="text-[20px] font-[350] text-(--Contrast-Blue,#0074AD)">
                                Add additional class
                              </span>
                            </button>
                          </div>
                        )}
                        <hr className="-variation-1" />
                      </div>
                    )}
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AllApplicationDetails;
